import { useRef, useState } from 'react';
import { api } from '../../shared/api/client';
import type { InventoryItem, InventoryLocation, InventoryMovement } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatQuantity, MOVEMENT_KIND } from './labels';
import { MovementForm, NewItemForm, NewLocationForm, type MovementChoice } from './StockForms';
import { LoansCard } from './LoansCard';

type Panel = MovementChoice | 'item' | 'location' | null;
const MOVE_TITLE: Record<MovementChoice, string> = { entry: 'Entrada', exit: 'Salida', more: 'Más movimientos' };
type Overview = { items: InventoryItem[]; locations: InventoryLocation[] };

/** Inventario (Motor 19): UN inventario de la iglesia; la existencia sale de los movimientos. */
export function InventoryPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('inventory.manage');
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const overview = useAsync(
    (signal) => (churchId ? api.get<Overview>(`/inventory/churches/${churchId}/stock`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, version],
  );
  const last = useRef<Overview | null>(null);
  if (overview.status === 'ready') last.current = overview.data;
  const data = overview.status === 'ready' ? overview.data : last.current;

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const done = (message: string) => {
    setNotice(message);
    setVersion((v) => v + 1);
  };
  const canMove = !!data && data.items.length > 0 && data.locations.length > 0;
  const toggle = (p: Panel) => {
    setNotice(null);
    setPanel((current) => (current === p ? null : p));
  };

  return (
    <div className="page">
      <header className="page__header">
        <h1>Inventario</h1>
        <p className="muted">Lo que tiene la iglesia y dónde está.</p>
      </header>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {overview.status === 'loading' && !data ? <Loading /> : null}
      {overview.status === 'error' ? <ErrorState error={overview.error} onRetry={overview.reload} /> : null}
      {data ? (
        <>
          {canManage ? (
            <div className="actions actions--wrap">
              {(['entry', 'exit', 'more'] as const).map((c) => (
                <Button key={c} variant={panel === c ? 'primary' : c === 'more' ? 'ghost' : 'secondary'} onClick={() => toggle(c)} disabled={!canMove}>
                  {MOVE_TITLE[c]}
                </Button>
              ))}
              <Button variant="ghost" onClick={() => toggle('item')}>
                Nuevo artículo
              </Button>
              <Button variant="ghost" onClick={() => toggle('location')}>
                Nuevo lugar
              </Button>
            </div>
          ) : null}
          {canManage && !canMove ? <p className="muted small">Primero crea un artículo y un lugar.</p> : null}
          {panel === 'entry' || panel === 'exit' || panel === 'more' ? (
            <Card title={MOVE_TITLE[panel]}>
              <MovementForm key={panel} churchId={churchId} items={data.items} locations={data.locations} choice={panel} onDone={done} />
            </Card>
          ) : null}
          {panel === 'item' ? (
            <Card title="Nuevo artículo">
              <NewItemForm churchId={churchId} onDone={done} />
            </Card>
          ) : null}
          {panel === 'location' ? (
            <Card title="Nuevo lugar">
              <NewLocationForm churchId={churchId} onDone={done} />
            </Card>
          ) : null}
          {canManage && data.locations.length === 0 ? <Alert tone="info">Empieza creando un lugar (por ejemplo, Bodega) y luego tus artículos.</Alert> : null}
          <Card title="Existencias">
            {data.items.length === 0 ? <EmptyState title="Aún no hay artículos." /> : null}
            <ul className="list">
              {data.items.map((i) => (
                <ItemRow key={i.id} churchId={churchId} item={i} locations={data.locations} version={version} canManage={canManage} onDone={done} />
              ))}
            </ul>
          </Card>
          {data.locations.length ? (
            <Card title="Lugares">
              <ul className="list">
                {data.locations.map((l) => (
                  <LocationRow key={l.id} churchId={churchId} location={l} canManage={canManage} onDone={done} />
                ))}
              </ul>
            </Card>
          ) : null}
          <LoansCard churchId={churchId} items={data.items} locations={data.locations} canManage={canManage} onChange={() => setVersion((v) => v + 1)} />
        </>
      ) : null}
    </div>
  );
}

function LocationRow({ churchId, location: l, canManage, onDone }: { churchId: string; location: InventoryLocation; canManage: boolean; onDone: (m: string) => void }) {
  const [editing, setEditing] = useState(false);
  return (
    <li className="list__item list__item--actions">
      <span className="list__link">
        <strong>{l.name}</strong>
      </span>
      {canManage && !editing ? (
        <Button variant="ghost" onClick={() => setEditing(true)} aria-label={`Editar ${l.name}`}>
          Editar
        </Button>
      ) : null}
      {editing ? <NewLocationForm churchId={churchId} location={l} onCancel={() => setEditing(false)} onDone={(m) => { setEditing(false); onDone(m); }} /> : null}
    </li>
  );
}

function ItemRow({ churchId, item: i, locations, version, canManage, onDone }: { churchId: string; item: InventoryItem; locations: InventoryLocation[]; version: number; canManage: boolean; onDone: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const names = new Map(locations.map((l) => [l.id, l.name]));
  const where = i.byLocation.map((s) => `${names.get(s.locationId) ?? '—'}: ${formatQuantity(s.quantity)}`).join(' · ');
  return (
    <li className="list__item list__item--actions stock">
      <span className="list__link">
        <strong>
          {i.name} {i.low ? <span className="badge badge--warn">Poca existencia</span> : null}
        </strong>
        <span className="muted small">{where || 'Sin existencias'}</span>
      </span>
      <strong className="money">
        {formatQuantity(i.total)} {i.unit}
      </strong>
      <Button variant="ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={`Historial de ${i.name}`}>
        {open ? 'Ocultar' : 'Historial'}
      </Button>
      {canManage && !editing ? (
        <Button variant="ghost" onClick={() => setEditing(true)} aria-label={`Editar ${i.name}`}>
          Editar
        </Button>
      ) : null}
      {editing ? <NewItemForm churchId={churchId} item={i} onCancel={() => setEditing(false)} onDone={(m) => { setEditing(false); onDone(m); }} /> : null}
      {open ? <History churchId={churchId} itemId={i.id} version={version} /> : null}
    </li>
  );
}

function History({ churchId, itemId, version }: { churchId: string; itemId: string; version: number }) {
  const list = useAsync(
    (signal) => api.get<{ items: InventoryMovement[] }>(`/inventory/churches/${churchId}/movements?itemId=${itemId}`, signal).then((r) => r.items),
    [churchId, itemId, version],
  );
  return (
    <div className="stock__history">
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && list.data.length === 0 ? <p className="muted small">Sin movimientos.</p> : null}
      {list.status === 'ready' ? (
        <ul className="list">
          {list.data.map((m) => (
            <li key={m.id} className="list__item list__item--actions">
              <span className="small">
                {MOVEMENT_KIND[m.kind]} · {m.location.name}
                {m.note ? ` · ${m.note}` : ''}
                <span className="muted"> · {new Date(m.recordedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })}, {m.recordedBy.name}</span>
              </span>
              <span className={m.quantity < 0 ? 'money money--negative small' : 'money small'}>
                {m.quantity > 0 ? '+' : ''}
                {formatQuantity(m.quantity)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
